import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';
import {
  usePublicConfig,
  useGiveSheet,
  useGivingState,
  closeGiveSheet,
  restGiving,
  markGiveOut,
  noteDeliberateLeave,
} from '../utils/giving';
import { buildGiveLink, otherWaysUrl, inAppBrowser, restUntilWindowEnd, ALREADY_MS, COPY, TRACK } from '../utils/giveCore';
import './GiveSheet.css';

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

const isCoarsePointer = () =>
  typeof window !== 'undefined' && Boolean(window.matchMedia) && window.matchMedia('(pointer: coarse)').matches;

/** A home-screen install (iOS, or any installed app window): the host decides where a link opens. */
function installedApp() {
  try {
    return window.navigator.standalone === true
      || (Boolean(window.matchMedia) && window.matchMedia('(display-mode: standalone)').matches);
  } catch (e) {
    return false;
  }
}

/** Focuses `el` if it can take focus (still on the page, shown, not hidden away); true when it did. */
function focusIfShown(el) {
  if (!el || !el.isConnected || !el.getClientRects().length) return false;
  el.focus({ preventScroll: true });
  return document.activeElement === el;
}

/**
 * Where focus goes when the sheet closes: the link that opened it; if an
 * answer was redrawn meanwhile, the newest link of the same kind; else the
 * menu button (a phone's menu closed itself first); else the box, but never
 * on a touch screen, where it would raise the keyboard for nothing.
 */
function giveFocusBack({ opener, surface }, menuButton) {
  if (focusIfShown(opener)) return;
  if ([...document.querySelectorAll(`[data-give-opener="${surface}"]`)].reverse().some(focusIfShown)) return;
  if (focusIfShown(menuButton)) return;
  if (!isCoarsePointer()) [...document.querySelectorAll('.cv-input')].some(focusIfShown);
}

/**
 * The support sheet (utils/giving.js has the policy): Ask Pastor Rob is free,
 * what an answer costs The Net, and two equal links to The Net's own Pushpay
 * page, Give once and Give monthly. Nothing is preselected, no amount is set
 * (Pushpay's field starts blank), and nothing about the person rides in the
 * link. Pushpay can't be framed, so the sheet explains and hands off: each
 * Give button is a real link the tap itself follows, in a new tab where the
 * browser allows it.
 *
 * One is mounted (AppShell), portaled to the page like the Genius reader, and
 * opened only by a tap (openGiveFromClick). A real dialog: focus moves to the
 * title, Tab stays inside, and Escape, Close, "Not now" and the scrim close
 * it; from its first screen that rests the asks for the rest of the window.
 * Focus then goes back to what opened it.
 *
 * Its screens: the ask (Waiting while an answer is still arriving: leaving
 * mid-answer can cut the answer off), then Returned after a tap on Give (it
 * thanks them for the thought, never for a gift nobody can see), or Already
 * after "I already give to The Net". A crisis card closes it.
 */
function GiveSheet({ fallbackFocusRef }) {
  const sheet = useGiveSheet();
  if (!sheet) return null;
  // A fresh one each time it opens, so reopening always starts at the ask.
  return <SupportSheet key={sheet.n} sheet={sheet} fallbackFocusRef={fallbackFocusRef} />;
}

function SupportSheet({ sheet, fallbackFocusRef }) {
  const cfg = usePublicConfig();
  const { ask, answering, resetAt } = useGivingState();
  const [screen, setScreen] = useState('ask'); // 'ask' | 'returned' | 'already'
  const [wentTo, setWentTo] = useState(null); // the link that went to Pushpay
  const dialogRef = useRef(null);
  const titleRef = useRef(null);
  const live = useRef({}); // the latest of these, for handlers made before a render
  live.current = { screen, answering, resetAt };

  const once = buildGiveLink(cfg, { monthly: false });
  const monthly = buildGiveLink(cfg, { monthly: true });
  const inApp = inAppBrowser(typeof navigator !== 'undefined' ? navigator.userAgent : '');
  // Facebook, Instagram and a home-screen install decide for themselves where it opens.
  const promisesTab = !inApp && !installedApp();

  const finish = useCallback(() => {
    closeGiveSheet();
    setTimeout(() => giveFocusBack(sheet, fallbackFocusRef ? fallbackFocusRef.current : null), 0);
  }, [sheet, fallbackFocusRef]);

  // Close, "Not now", Escape or the scrim. From the ask itself, the asks rest
  // for the rest of this window; after a Give tap or "already" they rest already.
  const dismiss = useCallback(() => {
    if (live.current.screen === 'ask') restGiving(restUntilWindowEnd(Date.now(), live.current.resetAt));
    finish();
  }, [finish]);

  // First focus on the title, never on a button; again on each new screen.
  useEffect(() => {
    const t = setTimeout(() => titleRef.current?.focus({ preventScroll: true }), 30);
    return () => clearTimeout(t);
  }, [screen]);

  // A crisis card (or the server's hold) while it is open: no ask, so it goes.
  useEffect(() => {
    if (ask === 'hold') finish();
  }, [ask, finish]);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      dismiss();
      return;
    }
    const box = dialogRef.current;
    if (e.key !== 'Tab' || !box) return;
    const items = [...box.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    const at = document.activeElement;
    if (!box.contains(at)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); }
    else if (e.shiftKey && at === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && at === last) { e.preventDefault(); first.focus(); }
  }, [dismiss]);

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onKeyDown]);

  /**
   * Give once or Give monthly. The tap itself goes to Pushpay: no scripted
   * pop-up and nothing awaited first, or Safari loses the tap's permission.
   * This only writes the device's markers, and turns to Returned on the next
   * tick, so the link is never taken away during its own click. While an
   * answer is still arriving, the tap waits.
   */
  const go = (e, href) => {
    if (live.current.answering) {
      e.preventDefault();
      return;
    }
    markGiveOut();
    restGiving(restUntilWindowEnd(Date.now(), live.current.resetAt));
    setTimeout(() => {
      setWentTo(href);
      setScreen('returned');
    }, 0);
  };

  const already = () => {
    restGiving(Date.now() + ALREADY_MS);
    setScreen('already');
  };

  const giveLink = (href, label, track) => (
    <a
      className="apr-btn apr-btn--lg give-btn"
      href={href}
      target="_blank"
      rel="noopener"
      aria-describedby="give-opens give-wait"
      aria-disabled={answering ? 'true' : undefined}
      data-track={track}
      onClick={(e) => go(e, href)}
      onAuxClick={(e) => { if (live.current.answering) e.preventDefault(); }}
    >
      {label}
      <Icon name="external-link" size={16} />
    </a>
  );

  return createPortal(
    <div className="apr-overlay give-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) dismiss(); }}>
      <div
        ref={dialogRef}
        className="apr-dialog give-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="give-title"
        aria-describedby="give-lead"
      >
        <span className="apr-dialog-grabber" aria-hidden="true" />
        <button type="button" className="apr-icon-btn apr-icon-btn--bare apr-dialog-close" onClick={dismiss} aria-label={COPY.close} data-track={TRACK.close}>
          <Icon name="x" size={20} />
        </button>

        {screen === 'ask' ? (
          <>
            <h2 key="ask" ref={titleRef} id="give-title" className="apr-dialog-title give-title" tabIndex={-1}>{COPY.title}</h2>
            {sheet.surface === 'limit' && (
              <p className="give-limit">
                <Icon name="clock" size={16} />
                <span>{COPY.limit(sheet.limitTime)}</span>
              </p>
            )}
            <p id="give-lead" className="give-lead">{COPY.lead(cfg.answerCents)}</p>
            <div className="give-actions">
              {giveLink(once, COPY.giveOnce, TRACK.once)}
              {giveLink(monthly, COPY.giveMonthly, TRACK.monthly)}
            </div>
            <p id="give-opens" className="give-small">{promisesTab ? `${COPY.opens} ${COPY.opensTab}` : COPY.opens}</p>
            <p id="give-wait" className="give-small give-wait" role="status">{answering ? COPY.wait : ''}</p>
            {inApp && (
              <p className="give-hint">
                <Icon name="info" size={16} />
                <span>{COPY.hint}</span>
              </p>
            )}
            <p className="give-small give-fine">
              {COPY.fine(cfg.giveFund)}{' '}
              <a
                href={otherWaysUrl(cfg.giveUrl)}
                target="_blank"
                rel="noopener"
                data-track={TRACK.otherWays}
                // Like the Give buttons, it waits for an answer still arriving: leaving can cut it off.
                aria-disabled={answering ? 'true' : undefined}
                aria-describedby="give-wait"
                onClick={(e) => { if (live.current.answering) e.preventDefault(); }}
                onAuxClick={(e) => { if (live.current.answering) e.preventDefault(); }}
              >
                {COPY.otherWays}
              </a>
            </p>
            <div className="give-choices">
              <button type="button" className="apr-btn apr-btn--text" onClick={dismiss} data-track={TRACK.notNow}>{COPY.notNow}</button>
              <button type="button" className="apr-btn apr-btn--text" onClick={already} data-track={TRACK.already}>{COPY.already}</button>
            </div>
          </>
        ) : (
          <div className="give-thanks" role="status">
            <h2 key={screen} ref={titleRef} id="give-title" className="apr-dialog-title give-title" tabIndex={-1}>
              {screen === 'returned' ? COPY.returnedTitle : COPY.alreadyTitle}
            </h2>
            <p id="give-lead" className="give-lead">{screen === 'returned' ? COPY.returnedText : COPY.alreadyText}</p>
            <div className="give-done">
              <button type="button" className="apr-btn give-done-btn" onClick={finish} data-track={TRACK.done}>{COPY.done}</button>
              {screen === 'returned' && wentTo && (
                // The same page in this tab, for a browser that didn't open the new one.
                <a className="give-again" href={wentTo} onClick={() => { markGiveOut(); noteDeliberateLeave(); }} data-track={TRACK.again}>{COPY.again}</a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default GiveSheet;
